/*
 * Shooting-star overlay for the stargaze minigame.
 *
 * One container, one Graphics under the hood — every star is redrawn each
 * frame because they're tiny and few (<10 on screen at any one time).
 *
 * A star is a bright cream-white head with a soft glow ring and a short fading
 * tail of sparks behind it along its velocity vector. Stars travel in straight
 * lines across the upper third of the sky (y ~30..180) at ~120 px/sec, live
 * for ~1.2–1.8s, and fade out the last ~0.4s of their life.
 *
 * The renderer owns no game-rule state (no scoring, no hit-test). The
 * minigame module is the source of truth; this module just spawns, moves,
 * and draws the visual particles. The hit-test helper in
 * minigames/stargaze.ts reads from `activeStars()`.
 */

import { Container, Graphics } from 'pixi.js';

const HEAD_COLOR = 0xfff7e0; // bright cream-white
const GLOW_COLOR = 0xffe9b3; // warm lantern-tinted glow

/** One in-flight shooting star particle. */
export interface ShootingStar {
  /** Stable id for the lifetime of the particle (for hit dedupe). */
  id: number;
  x: number;
  y: number;
  /** Velocity (px/sec). */
  vx: number;
  vy: number;
  /** Seconds remaining before despawn. */
  life: number;
  /** Total lifetime (for tail-fade easing). */
  maxLife: number;
  /** Whether the player has already hit this one (so the renderer can fade fast). */
  hit: boolean;
}

/** Options passed to spawn(). All fields optional; sensible defaults applied. */
export interface ShootingStarSpawnOpts {
  /** Override start position. Defaults to a random spot on the left edge of the sky band. */
  x?: number;
  y?: number;
  /** Override velocity. Defaults to ~120 px/sec at a shallow downward angle. */
  vx?: number;
  vy?: number;
  /** Override lifetime in seconds. Default 1.2–1.8s random. */
  life?: number;
}

export interface ShootingStarsView {
  container: Container;
  spawn(opts?: ShootingStarSpawnOpts): ShootingStar;
  update(dtSec: number): void;
  clear(): void;
  activeStars(): ShootingStar[];
}

/** Upper-third-of-sky vertical band for shooting-star travel. */
const SKY_BAND_MIN_Y = 30;
const SKY_BAND_MAX_Y = 180;
const WORLD_WIDTH = 600;

const DEFAULT_SPEED = 120; // px/sec
const HIT_FADE_SEC = 0.18; // fast fade-out once a player hits the star

let _nextId = 1;

export function createShootingStars(): ShootingStarsView {
  const root = new Container();
  const g = new Graphics();
  root.addChild(g);

  const stars: ShootingStar[] = [];

  function spawn(opts: ShootingStarSpawnOpts = {}): ShootingStar {
    // Random angle 10°–25° below horizontal, mostly rightward — feels meteor-y.
    const angle = (10 + Math.random() * 15) * (Math.PI / 180);
    const dir = Math.random() < 0.5 ? 1 : -1; // either left-to-right or right-to-left
    const vx = opts.vx ?? Math.cos(angle) * DEFAULT_SPEED * dir;
    const vy = opts.vy ?? Math.sin(angle) * DEFAULT_SPEED;

    // Default start: just off the edge it's flying in from.
    const yStart = SKY_BAND_MIN_Y + Math.random() * (SKY_BAND_MAX_Y - SKY_BAND_MIN_Y - 60);
    const xStart =
      dir > 0 ? -20 + Math.random() * 40 : WORLD_WIDTH - 20 + Math.random() * 40;

    const life = opts.life ?? 1.2 + Math.random() * 0.6;

    const star: ShootingStar = {
      id: _nextId++,
      x: opts.x ?? xStart,
      y: opts.y ?? yStart,
      vx,
      vy,
      life,
      maxLife: life,
      hit: false,
    };
    stars.push(star);
    return star;
  }

  function update(dtSec: number): void {
    // Step + cull.
    for (let i = stars.length - 1; i >= 0; i--) {
      const s = stars[i]!;
      if (s.hit) {
        // Snap to fast fade once tapped.
        s.life = Math.min(s.life, HIT_FADE_SEC);
      }
      s.x += s.vx * dtSec;
      s.y += s.vy * dtSec;
      s.life -= dtSec;
      // Cull dead, or wandered well below the sky band.
      if (s.life <= 0 || s.y > SKY_BAND_MAX_Y + 40) {
        stars.splice(i, 1);
      }
    }

    // Redraw all surviving stars.
    g.clear();
    for (const s of stars) {
      const lifeFrac = Math.max(0, s.life / s.maxLife); // 1 → 0 over life
      // Fade in the first 0.15s and fade out the last 0.35s.
      const ageSec = s.maxLife - s.life;
      const fadeIn = Math.min(1, ageSec / 0.15);
      const fadeOut = Math.min(1, lifeFrac / 0.35);
      const alpha = Math.min(fadeIn, fadeOut);
      if (alpha <= 0) continue;

      // Tail: 6 small sparks behind the head, fading along the tail length.
      const tailLen = 18;
      const norm = Math.hypot(s.vx, s.vy) || 1;
      const ux = s.vx / norm;
      const uy = s.vy / norm;
      const sparks = 6;
      for (let i = 0; i < sparks; i++) {
        const t = i / sparks;
        const sx = s.x - ux * tailLen * t;
        const sy = s.y - uy * tailLen * t;
        const sparkA = alpha * (1 - t) * 0.55;
        g.circle(sx, sy, 1.2 * (1 - t * 0.6)).fill({
          color: HEAD_COLOR,
          alpha: sparkA,
        });
      }

      // Outer soft glow.
      g.circle(s.x, s.y, 7).fill({ color: GLOW_COLOR, alpha: alpha * 0.18 });
      g.circle(s.x, s.y, 4.5).fill({ color: GLOW_COLOR, alpha: alpha * 0.28 });
      // Bright head.
      g.circle(s.x, s.y, 3).fill({ color: HEAD_COLOR, alpha: alpha });
      // Tiny inner-hot core.
      g.circle(s.x, s.y, 1.2).fill({ color: 0xffffff, alpha: alpha });
    }
  }

  function clear(): void {
    stars.length = 0;
    g.clear();
  }

  function activeStars(): ShootingStar[] {
    return stars;
  }

  return { container: root, spawn, update, clear, activeStars };
}
